import { createSlice } from '@reduxjs/toolkit';
import { getRestaurantStorageItem, setRestaurantStorageItem } from '../utils/restaurantStorageUtils';

const APP_SETTINGS_KEY = 'appSettings';

// Load settings from restaurant-scoped localStorage
const loadSettings = (restaurantSlug) => {
  if (!restaurantSlug) {
    // Return default settings if no restaurant slug
    return {
      appTheme: 'teal',
      appDarkMode: false,
      appGradientEnabled: true,
      appHeaderFixed: true,
      appHeaderInverse: false,
      appSidebarFixed: true,
      appSidebarGrid: false,
    };
  }

  try {
    const storedSettings = getRestaurantStorageItem(restaurantSlug, APP_SETTINGS_KEY);
    if (storedSettings) {
      return storedSettings;
    }
  } catch (error) {
    console.error('Error loading app settings from localStorage:', error);
  }
  
  // Return default settings
  return {
    appTheme: 'teal',
    appDarkMode: false,
    appGradientEnabled: true, // Default to true as requested
    appHeaderFixed: true,
    appHeaderInverse: false,
    appSidebarFixed: true,
    appSidebarGrid: false,
  };
};

// Save settings to restaurant-scoped localStorage
const saveSettings = (restaurantSlug, settings) => {
  if (!restaurantSlug) {
    return;
  }
  try {
    setRestaurantStorageItem(restaurantSlug, APP_SETTINGS_KEY, settings);
  } catch (error) {
    console.error('Error saving app settings to localStorage:', error);
  }
};

// Store settings per restaurant: { [restaurantSlug]: settings }
const initialState = {
  settings: {},
  currentRestaurantSlug: null,
};

const appSettingsSlice = createSlice({
  name: 'appSettings',
  initialState,
  reducers: {
    // Initialize settings for a restaurant
    initializeSettings: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (restaurantSlug && !state.settings[restaurantSlug]) {
        state.settings[restaurantSlug] = loadSettings(restaurantSlug);
        state.currentRestaurantSlug = restaurantSlug;
      }
    },
    
    // Set current restaurant
    setCurrentRestaurant: (state, action) => {
      const { restaurantSlug } = action.payload;
      state.currentRestaurantSlug = restaurantSlug;
      if (restaurantSlug && !state.settings[restaurantSlug]) {
        state.settings[restaurantSlug] = loadSettings(restaurantSlug);
      }
    },
    
    setAppTheme: (state, action) => {
      const { restaurantSlug, theme } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        state.settings[restaurantSlug].appTheme = theme;
        saveSettings(restaurantSlug, state.settings[restaurantSlug]);
      }
    },
    setAppDarkMode: (state, action) => {
      const { restaurantSlug, darkMode } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        state.settings[restaurantSlug].appDarkMode = darkMode;
        saveSettings(restaurantSlug, state.settings[restaurantSlug]);
      }
    },
    setAppGradientEnabled: (state, action) => {
      const { restaurantSlug, enabled } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        state.settings[restaurantSlug].appGradientEnabled = enabled;
        saveSettings(restaurantSlug, state.settings[restaurantSlug]);
      }
    },
    setAppHeaderFixed: (state, action) => {
      const { restaurantSlug, fixed } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        state.settings[restaurantSlug].appHeaderFixed = fixed;
        saveSettings(restaurantSlug, state.settings[restaurantSlug]);
      }
    },
    setAppHeaderInverse: (state, action) => {
      const { restaurantSlug, inverse } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        state.settings[restaurantSlug].appHeaderInverse = inverse;
        saveSettings(restaurantSlug, state.settings[restaurantSlug]);
      }
    },
    setAppSidebarFixed: (state, action) => {
      const { restaurantSlug, fixed } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        state.settings[restaurantSlug].appSidebarFixed = fixed;
        saveSettings(restaurantSlug, state.settings[restaurantSlug]);
      }
    },
    setAppSidebarGrid: (state, action) => {
      const { restaurantSlug, grid } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        state.settings[restaurantSlug].appSidebarGrid = grid;
        saveSettings(restaurantSlug, state.settings[restaurantSlug]);
      }
    },
    resetAppSettings: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (restaurantSlug && state.settings[restaurantSlug]) {
        const defaultSettings = {
          appTheme: 'teal',
          appDarkMode: false,
          appGradientEnabled: true,
          appHeaderFixed: true,
          appHeaderInverse: false,
          appSidebarFixed: true,
          appSidebarGrid: false,
        };
        state.settings[restaurantSlug] = defaultSettings;
        saveSettings(restaurantSlug, defaultSettings);
      }
    },
  },
});

export const {
  initializeSettings,
  setCurrentRestaurant,
  setAppTheme,
  setAppDarkMode,
  setAppGradientEnabled,
  setAppHeaderFixed,
  setAppHeaderInverse,
  setAppSidebarFixed,
  setAppSidebarGrid,
  resetAppSettings,
} = appSettingsSlice.actions;

// Selector to get settings for current restaurant
export const selectAppSettings = (state, restaurantSlug) => {
  if (!restaurantSlug) {
    return {
      appTheme: 'teal',
      appDarkMode: false,
      appGradientEnabled: true,
      appHeaderFixed: true,
      appHeaderInverse: false,
      appSidebarFixed: true,
      appSidebarGrid: false,
    };
  }
  return state.appSettings.settings[restaurantSlug] || {
    appTheme: 'teal',
    appDarkMode: false,
    appGradientEnabled: true,
    appHeaderFixed: true,
    appHeaderInverse: false,
    appSidebarFixed: true,
    appSidebarGrid: false,
  };
};

export default appSettingsSlice.reducer;
